/**
 * Vocabulario do modulo Admin - Empresas num lugar so.
 *
 * Rotulos em `Record` com chave de uniao literal (nao index signature): se um
 * tipo compartilhado ganhar um valor novo (status, modelo de cobranca,
 * provedor), o tsc aponta o Record incompleto aqui em vez de renderizar
 * `undefined` na tela. Os textos sao os mesmos do modulo de configuracoes para
 * a empresa nao ler "Em implantação" numa tela e "Onboarding" na outra.
 *
 * Toda descricao de funil parte do FATO ja ocorrido (checkout abandonado ha
 * 30 min, assinatura ja vencida): nao existe lembrete antes de vencer nem
 * cobranca preventiva -- sem o fato nao ha recuperacao e nao ha comissao.
 */

import type { BadgeTone, IconName } from '../../components/ui';
import type { BillingModel, CompanyStatus, FunnelKind, LogoTone, RefundPolicy, UserRole } from '../../types';
import type { AbaDetalhe, AccountEventKind, ColunaLista, InvoiceStatus, NivelSaude, ProvedorIntegracao } from './types';

export const STATUS_EMPRESA: Readonly<Record<CompanyStatus, { readonly label: string; readonly plural: string; readonly tone: BadgeTone }>> = {
  ativa: { label: 'Ativa', plural: 'Ativas', tone: 'good' },
  onboarding: { label: 'Em implantação', plural: 'Em implantação', tone: 'warning' },
  pausada: { label: 'Pausada', plural: 'Pausadas', tone: 'neutral' },
};

export const STATUS_LISTA: readonly CompanyStatus[] = ['ativa', 'onboarding', 'pausada'];

export const MODELO_COBRANCA: Readonly<
  Record<BillingModel, { readonly label: string; readonly curto: string; readonly tone: BadgeTone; readonly explicacao: string }>
> = {
  contrato: {
    label: 'Contrato mensal',
    curto: 'Contrato',
    tone: 'info',
    explicacao: 'O Converte Fácil fatura a comissão do mês fechado e a empresa paga por boleto ou PIX.',
  },
  automatico: {
    label: 'Automático (split no provedor)',
    curto: 'Automático',
    tone: 'accent',
    explicacao: 'A comissão é retida na própria plataforma de checkout, a cada venda recuperada, sem fatura.',
  },
  a_definir: {
    label: 'A definir',
    curto: 'A definir',
    tone: 'warning',
    explicacao: 'Fica registrado como pendente; a cobrança só começa depois que o modelo for escolhido.',
  },
};

export const MODELOS_COBRANCA: readonly BillingModel[] = ['contrato', 'automatico', 'a_definir'];

/** Texto fixo que acompanha o modelo em toda tela (decisao do dono, 24/09/2026). */
export const NOTA_MODELO_PENDENTE =
  'Decisão pendente do dono: contrato mensal x automático ainda não foi fechado. O modelo registrado aqui é o previsto no cadastro, não uma cobrança em vigor.';

// -----------------------------------------------------------------------------
// Politica de estorno (ADR-001 E2, 24/09/2026)
// -----------------------------------------------------------------------------

export const POLITICA_PADRAO: RefundPolicy = 'reverte_chargeback';

export const POLITICAS_ESTORNO: readonly RefundPolicy[] = ['reverte_chargeback', 'nao_reverte', 'reverte_tudo'];

export const POLITICA_ESTORNO: Readonly<Record<RefundPolicy, { readonly label: string; readonly curto: string; readonly tone: BadgeTone; readonly explica: string }>> = {
  reverte_chargeback: {
    label: 'Só chargeback reverte (padrão)',
    curto: 'Só chargeback',
    tone: 'info',
    explica: 'Reembolso não devolve a comissão; chargeback (contestação no cartão) devolve. É a regra decidida para todas as empresas.',
  },
  nao_reverte: {
    label: 'Nunca reverte',
    curto: 'Nunca reverte',
    tone: 'neutral',
    explica: 'Reembolso e chargeback ficam registrados no extrato, mas a comissão já apurada permanece.',
  },
  reverte_tudo: {
    label: 'Reembolso e chargeback revertem',
    curto: 'Tudo reverte',
    tone: 'warning',
    explica: 'Qualquer estorno total devolve a comissão. Reembolso parcial continua sem reverter.',
  },
};

/** As regras da comissao que valem em qualquer politica (ADR-001), para a tela citar sem reescrever. */
export const REGRAS_COMISSAO: readonly string[] = [
  'Só venda recuperada: pagamento em até 7 dias depois de um contato do motor, com folga de 2 minutos para diferença de relógio.',
  'O crédito vai para o último contato do motor antes do pagamento.',
  'Pedido parcialmente pago credita o valor que de fato entrou.',
  'Venda fechada pela vendedora depois de um toque da régua continua sendo do Converte Fácil; "fechado por" é informação adicional.',
  'Venda sem webhook (Pix manual, outro link) entra como venda assistida, pelo mesmo caminho de atribuição.',
];

// -----------------------------------------------------------------------------
// Provedores de webhook: tres dedicados, o generico e a onda 1 (beta)
// -----------------------------------------------------------------------------

/** Ordem canonica do motor (`PROVIDERS` em src/domain/types.ts). */
export const PROVEDORES: readonly ProvedorIntegracao[] = ['kiwify', 'hotmart', 'shopify', 'generic', 'hubla', 'eduzz', 'cakto', 'greenn'];

export const PROVEDOR_INFO: Readonly<
  Record<ProvedorIntegracao, { readonly label: string; readonly curto: string; readonly hint: string; readonly segredoHint: string; readonly descricao: string }>
> = {
  kiwify: {
    label: 'Kiwify',
    curto: 'Kiwify',
    hint: 'Cole em Apps > Webhooks, marcando carrinho abandonado e compra aprovada.',
    segredoHint: 'Token gerado pela Kiwify ao criar o webhook. O motor valida a assinatura HMAC de cada evento.',
    descricao: 'Infoprodutos e assinaturas.',
  },
  hotmart: {
    label: 'Hotmart',
    curto: 'Hotmart',
    hint: 'Cole em Ferramentas > Webhook (2.0), com os eventos de compra e de carrinho.',
    segredoHint: 'O "hottok" do Webhook 2.0 da Hotmart. A Hotmart não assina o corpo: o token é a própria credencial.',
    descricao: 'Infoprodutos e assinaturas.',
  },
  shopify: {
    label: 'Shopify',
    curto: 'Shopify',
    hint: 'Cole em Configurações > Notificações > Webhooks, nos tópicos de checkout e pedido.',
    segredoHint: 'Segredo do app da Shopify, usado no cabeçalho X-Shopify-Hmac-SHA256.',
    descricao: 'Loja virtual com checkout abandonado nativo.',
  },
  generic: {
    label: 'Genérico (checkout próprio)',
    curto: 'Genérico',
    hint: 'Qualquer sistema que faça POST com JSON: checkout próprio, gateway, n8n, Make ou Zapier. Envie o contrato canônico do Converte Fácil.',
    segredoHint: 'Segredo do HMAC-SHA256 em hex sobre o corpo cru, enviado no cabeçalho X-CF-Signature.',
    descricao: 'Checkout próprio ou qualquer plataforma ainda sem adaptador.',
  },
  hubla: {
    label: 'Hubla',
    curto: 'Hubla',
    hint: 'Cole na regra de webhook da Hubla, com carrinho abandonado, fatura paga, reembolso, chargeback e assinatura desativada. Entregas de sandbox são descartadas.',
    segredoHint: 'Token da regra de webhook da Hubla, enviado no cabeçalho X-Hubla-Token. A Hubla não assina o corpo: o token é a própria credencial.',
    descricao: 'Infoprodutos e assinaturas. Adaptador em beta até o primeiro evento real.',
  },
  eduzz: {
    label: 'Eduzz',
    curto: 'Eduzz',
    hint: 'Cole no webhook da geração nova da Eduzz, com carrinho abandonado, fatura paga, reembolso, chargeback e contrato atrasado. O legado Órbita/MyEduzz não é aceito.',
    segredoHint: 'Chave de assinatura da Eduzz. O motor valida o HMAC-SHA256 do cabeçalho X-Signature sobre o corpo cru.',
    descricao: 'Infoprodutos e assinaturas (geração nova). Adaptador em beta até o primeiro evento real.',
  },
  cakto: {
    label: 'Cakto',
    curto: 'Cakto',
    hint: 'Cole no webhook da Cakto, com checkout abandonado, compra aprovada, reembolso, chargeback e assinatura atrasada ou cancelada.',
    segredoHint: 'Segredo do webhook da Cakto. O motor valida o HMAC-SHA256 do X-Cakto-Signature sobre o instante e o corpo cru, e recusa entrega fora da janela de tempo.',
    descricao: 'Infoprodutos e assinaturas. Adaptador em beta até o primeiro evento real.',
  },
  greenn: {
    label: 'Greenn',
    curto: 'Greenn',
    hint: 'Cole no webhook da API pública do vendedor da Greenn, com checkout abandonado, venda (paga, reembolsada, chargeback) e contrato.',
    segredoHint: 'Webhook Token da Greenn, enviado no cabeçalho X-Webhook-Token. A Greenn não assina o corpo: o token é a própria credencial.',
    descricao: 'Infoprodutos e assinaturas. Adaptador em beta até o primeiro evento real.',
  },
};

// -----------------------------------------------------------------------------
// Funis (sempre depois do fato)
// -----------------------------------------------------------------------------

export const FUNIL_INFO: Readonly<
  Record<FunnelKind, { readonly label: string; readonly icon: IconName; readonly fato: string; readonly descricao: string; readonly regua: readonly string[] }>
> = {
  carrinho_abandonado: {
    label: 'Carrinho abandonado',
    icon: 'carrinho',
    fato: 'Checkout abandonado há 30 min',
    descricao: 'Só depois do fato: entra quem iniciou o checkout e ficou 30 minutos sem pagar. Nenhum toque acontece antes do abandono.',
    regua: [
      'T+30min · Ligação da IA (até 2 tentativas)',
      'T+1h · WhatsApp + e-mail',
      'T+24h · WhatsApp + e-mail',
      'T+48h · WhatsApp + e-mail (último toque)',
    ],
  },
  expirados: {
    label: 'Expirados',
    icon: 'calendario',
    fato: 'Assinatura já vencida',
    descricao: 'Só depois do fato: entra a assinatura que já venceu e não foi renovada. Nada de lembrete antes de vencer. Exige ao menos uma oferta recorrente.',
    regua: ['D+15 · WhatsApp + e-mail', 'D+30 · WhatsApp + e-mail', 'D+60 · WhatsApp + e-mail', 'D+90 · WhatsApp + e-mail (último toque)'],
  },
};

export const FUNIS: readonly FunnelKind[] = ['carrinho_abandonado', 'expirados'];

// -----------------------------------------------------------------------------
// Detalhe
// -----------------------------------------------------------------------------

export const ABAS_DETALHE: readonly { readonly value: AbaDetalhe; readonly label: string; readonly icon: IconName }[] = [
  { value: 'resumo', label: 'Resumo', icon: 'painel' },
  { value: 'ofertas', label: 'Ofertas', icon: 'carrinho' },
  { value: 'integracoes', label: 'Integrações', icon: 'link' },
  { value: 'equipe', label: 'Equipe', icon: 'usuarios' },
  { value: 'comissao', label: 'Comissão e cobrança', icon: 'comissao' },
  { value: 'historico', label: 'Histórico', icon: 'relogio' },
];

export function ehAbaDetalhe(valor: string | null): valor is AbaDetalhe {
  return ABAS_DETALHE.some((a) => a.value === valor);
}

export const STATUS_FATURA: Readonly<Record<InvoiceStatus, { readonly label: string; readonly tone: BadgeTone }>> = {
  a_faturar: { label: 'A faturar', tone: 'warning' },
  faturado: { label: 'Faturado', tone: 'info' },
  pago: { label: 'Pago', tone: 'good' },
};

export const EVENTO_CONTA: Readonly<Record<AccountEventKind, { readonly icon: IconName; readonly tone: BadgeTone }>> = {
  criada: { icon: 'empresa', tone: 'brand' },
  integracao: { icon: 'link', tone: 'info' },
  equipe: { icon: 'usuarios', tone: 'neutral' },
  primeiro_webhook: { icon: 'raio', tone: 'accent' },
  primeira_recuperacao: { icon: 'moeda', tone: 'good' },
  ajuste: { icon: 'config', tone: 'neutral' },
};

export const ROLE_INFO: Readonly<Record<UserRole, { readonly label: string; readonly plural: string; readonly tone: BadgeTone; readonly hint: string }>> = {
  owner: { label: 'Dono', plural: 'Donos', tone: 'brand', hint: 'Vê tudo, altera configurações e convida pessoas.' },
  member: { label: 'Equipe', plural: 'Equipe', tone: 'neutral', hint: 'Vê funis, leads e contatos; não altera configurações.' },
  comercial: {
    label: 'Comercial',
    plural: 'Comercial',
    tone: 'accent',
    hint: 'Vendedora: atende quem respondeu a um toque da régua e registra venda assistida. A comissão continua creditada ao último toque do motor.',
  },
  platform_admin: { label: 'Converte Fácil', plural: 'Converte Fácil', tone: 'violet', hint: 'Equipe da plataforma; não é convidada por aqui.' },
};

export const ROLES_CONVIDAVEIS: readonly Exclude<UserRole, 'platform_admin'>[] = ['owner', 'member', 'comercial'];

// -----------------------------------------------------------------------------
// Saude
// -----------------------------------------------------------------------------

export const NIVEL_SAUDE: Readonly<Record<NivelSaude, { readonly label: string; readonly tone: BadgeTone; readonly icon: IconName; readonly explica: string }>> = {
  saudavel: { label: 'Saudável', tone: 'good', icon: 'check', explica: 'Integrações em dia e régua trazendo vendas de volta.' },
  atencao: { label: 'Atenção', tone: 'warning', icon: 'alerta', explica: 'Algo está freando a recuperação; ainda há venda entrando.' },
  critica: { label: 'Crítica', tone: 'critical', icon: 'alerta', explica: 'A régua está perdendo leads agora: integração quebrada ou canal fora do ar.' },
  implantacao: { label: 'Implantação', tone: 'info', icon: 'config', explica: 'Conta nova: faltam passos para a régua rodar sozinha.' },
  pausada: { label: 'Pausada', tone: 'neutral', icon: 'pausa', explica: 'Nenhum lead novo entra na régua enquanto estiver pausada.' },
};

export const NIVEIS_SAUDE: readonly NivelSaude[] = ['critica', 'atencao', 'implantacao', 'saudavel', 'pausada'];

/** Peso para ordenar por saude: o mais grave primeiro na ordem decrescente. */
export const PESO_SAUDE: Readonly<Record<NivelSaude, number>> = {
  critica: 5,
  atencao: 4,
  implantacao: 3,
  pausada: 2,
  saudavel: 1,
};

// -----------------------------------------------------------------------------
// Lista: colunas e ordens
// -----------------------------------------------------------------------------

export const COLUNA_INFO: Readonly<Record<ColunaLista, { readonly rotulo: string; readonly titulo: string; readonly numerica: boolean; readonly descPadrao: boolean }>> = {
  empresa: { rotulo: 'Empresa', titulo: 'Nome da empresa, em ordem alfabética', numerica: false, descPadrao: false },
  saude: { rotulo: 'Saúde', titulo: 'Crítica primeiro: integração quebrada ou canal fora do ar perdem leads agora', numerica: false, descPadrao: true },
  recuperado: { rotulo: 'Recuperado', titulo: 'Valor pago pelos leads que a régua trouxe de volta no período (base da comissão)', numerica: true, descPadrao: true },
  variacao: { rotulo: 'Variação', titulo: 'Diferença do recuperado contra o período anterior de mesmo tamanho', numerica: true, descPadrao: true },
  comissao: { rotulo: 'Comissão', titulo: 'O que o Converte Fácil recebe sobre o recuperado, pelo percentual de cada empresa', numerica: true, descPadrao: true },
  taxa: { rotulo: 'Taxa', titulo: 'Vendas recuperadas sobre checkouts abandonados que entraram na régua', numerica: true, descPadrao: true },
  vendas: { rotulo: 'Vendas', titulo: 'Quantidade de pagamentos creditados à régua no período', numerica: true, descPadrao: true },
  criada: { rotulo: 'Criada em', titulo: 'Data do cadastro da empresa', numerica: false, descPadrao: true },
};

export const ORDENS_DISPONIVEIS: readonly { readonly valor: string; readonly coluna: ColunaLista; readonly desc: boolean; readonly label: string }[] = [
  { valor: 'recuperado-desc', coluna: 'recuperado', desc: true, label: 'Mais recuperado' },
  { valor: 'comissao-desc', coluna: 'comissao', desc: true, label: 'Mais comissão' },
  { valor: 'variacao-desc', coluna: 'variacao', desc: true, label: 'Maior alta vs anterior' },
  { valor: 'variacao-asc', coluna: 'variacao', desc: false, label: 'Maior queda vs anterior' },
  { valor: 'taxa-desc', coluna: 'taxa', desc: true, label: 'Maior taxa de recuperação' },
  { valor: 'saude-desc', coluna: 'saude', desc: true, label: 'Precisa de atenção primeiro' },
  { valor: 'vendas-desc', coluna: 'vendas', desc: true, label: 'Mais vendas' },
  { valor: 'criada-desc', coluna: 'criada', desc: true, label: 'Cadastro mais recente' },
  { valor: 'empresa-asc', coluna: 'empresa', desc: false, label: 'Nome (A–Z)' },
];

// -----------------------------------------------------------------------------
// Marca
// -----------------------------------------------------------------------------

export const TONES: readonly LogoTone[] = [1, 2, 3, 4, 5, 6, 7, 8];

export const TONE_NAME: Readonly<Record<LogoTone, string>> = {
  1: 'Azul',
  2: 'Laranja',
  3: 'Água',
  4: 'Amarelo',
  5: 'Magenta',
  6: 'Verde',
  7: 'Violeta',
  8: 'Vermelho',
};

export const TONE_CLASS: Readonly<Record<LogoTone, string>> = {
  1: 'bg-series-1',
  2: 'bg-series-2',
  3: 'bg-series-3',
  4: 'bg-series-4',
  5: 'bg-series-5',
  6: 'bg-series-6',
  7: 'bg-series-7',
  8: 'bg-series-8',
};

// -----------------------------------------------------------------------------
// Validacao simples
// -----------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailValido(valor: string): boolean {
  return EMAIL_RE.test(valor.trim());
}

/** Aceita "https://loja.exemplo" e "loja.exemplo"; rejeita texto sem ponto. Vazio e valido (campo opcional). */
export function siteValido(valor: string): boolean {
  const texto = valor.trim();
  if (texto === '') {
    return true;
  }
  return /^(https?:\/\/)?[^\s/]+\.[^\s]+$/.test(texto);
}

/** Sem acento e em minusculas: "CodeLab" e "codelab", "Implantação" e "implantacao" se encontram. */
export function normalizar(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}
